import { curryAppSelector } from "@/store/reselect";
import { EMPTY_ARRAY, assert, walkWithQueue } from "@faro-lotv/foundation";
import {
  GUID,
  IElementGenericPointCloudStream,
  IElementImg360,
  isIElementGenericPointCloudStream,
  isIElementImg360,
  isIElementImg360GrayScale,
} from "@faro-lotv/ielement-types";
import { selectLoadedIElements } from "@faro-lotv/project-source";
import {
  CaptureTreeEntityType,
  CaptureTreePointCloudType,
  RevisionScanEntity,
  isDeletedRevisionStatus,
  isRevisionScanEntity,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import {
  selectRevisionEntities,
  selectRevisionEntity,
  selectRevisionEntityChildrenMap,
  selectRevisionEntityMap,
} from "./revision-entity-selectors";
import {
  getPointCloudStreamForScanEntity,
  isEntityDeletedOnRevision,
} from "./selector-helpers";

/**
 * @param state The current application state.
 * @returns All loaded point cloud scans in the revision.
 */
export const selectRevisionScans = createSelector(
  [selectRevisionEntities],
  (revisionEntities) => revisionEntities.filter(isRevisionScanEntity),
);

/**
 * @param state The current application state.
 * @returns Whether the revision contains Rovi data, which is required for Gaussian splats generation.
 */
export const selectHasRoviScans = createSelector(
  [selectRevisionScans],
  (scans) =>
    scans.some(
      (scan) =>
        !isEntityDeletedOnRevision(scan) &&
        // Rovi data can also be ingested as a generic scan holding a raw Rovi point cloud.
        (scan.type === CaptureTreeEntityType.roviScan ||
          !!scan.pointClouds?.some(
            (pointCloud) =>
              pointCloud.type === CaptureTreePointCloudType.roviRaw,
          )),
    ),
);

/**
 * @param state The current application state.
 * @param scanEntity The scan entity to get the point cloud stream for.
 * @returns The point cloud stream corresponding to the scan entity.
 */
export const selectPointCloudStreamForScanEntity = curryAppSelector(
  createSelector(
    [
      selectLoadedIElements,
      (state, scanEntity?: RevisionScanEntity) => scanEntity,
    ],
    (iElements, scanEntity): IElementGenericPointCloudStream | undefined => {
      if (!scanEntity) return;
      return getPointCloudStreamForScanEntity(
        iElements.filter(isIElementGenericPointCloudStream),
        scanEntity.id,
      );
    },
  ),
);

/**
 * @param state The current application state.
 * @param scanEntity The scan entity to get the img360 for.
 * @returns The img360 element for the scan, preferring color images over grayscale.
 */
export const selectImg360ForScanEntity = curryAppSelector(
  createSelector(
    [
      selectLoadedIElements,
      (state, scanEntity?: RevisionScanEntity) => scanEntity,
    ],
    (iElements, scanEntity): IElementImg360 | undefined => {
      if (!scanEntity) return;

      // ASSUMPTIONS:
      // - The scan entity of the revision has the same ID as the data set IElement in the Capture Tree
      // - The Img360 element is a direct child of that data set IElement
      const img360s = iElements
        .filter(isIElementImg360)
        .filter((iElement) => iElement.parentId === scanEntity.id);

      // Prefer the color image, fall back to the grayscale image for intensity scans.
      return (
        img360s.find((iElement) => !isIElementImg360GrayScale(iElement)) ??
        img360s.find((iElement) => isIElementImg360GrayScale(iElement))
      );
    },
  ),
);

/**
 * @param state The current application state.
 * @param id The ID of the scan entity
 * @returns The scan entity with the given ID or `undefined` if it's not loaded.
 * @throws an assertion error if the entity is not a valid scan.
 */
export const selectRevisionEntityScan = curryAppSelector(
  createSelector(
    [(state, id: GUID | undefined) => selectRevisionEntity(id)(state)],
    (revisionEntity): RevisionScanEntity | undefined => {
      if (!revisionEntity) return;

      assert(isRevisionScanEntity(revisionEntity));
      return revisionEntity;
    },
  ),
);

/**
 * @param _state The current application state.
 * @param entityId The ID of the entity to get the scan descendants for.
 * @returns All descendants of the entity which are scans.
 *  The scans are returned in breadth-first order.
 */
export const selectRevisionEntityDescendantScans = curryAppSelector(
  createSelector(
    [
      selectRevisionEntityMap,
      selectRevisionEntityChildrenMap,
      (_state, entityId?: GUID) => entityId,
    ],
    (entityMap, childrenMap, entityId?: GUID) => {
      if (!entityId) return EMPTY_ARRAY;

      const entity = entityMap[entityId];
      if (!entity) return EMPTY_ARRAY;

      const scans: RevisionScanEntity[] = [];

      walkWithQueue([entity], (current, append) => {
        if (!isDeletedRevisionStatus(current.status)) {
          if (isRevisionScanEntity(current)) {
            scans.push(current);
          }
        }

        const children = childrenMap[current.id];
        if (children) append(...children);
      });

      return scans.length ? scans : EMPTY_ARRAY;
    },
  ),
);
